export const lightModeColors = {
  get: ['#10B981', '#E8F6F0'], // Green with light background
  post: ['#3B82F6', 'rgb(198 220 255)'], // Blue with light background
  put: ['#F59E0B', 'rgb(251 230 195)'], // Orange with light background
  delete: ['#EF4444', '#f7cccc'], // Red with light background
  default: ['#666', '#F3F4F6'], // Default gray colors
};

export const darkModeColors = {
  get: ['#059669', '#1C3A2D'], // Dark green with deep green background
  post: ['#2563EB', '#1E3A8A'], // Bright blue with dark blue background
  put: ['#D97706', '#4B3A27'], // Deep orange with brownish background
  delete: ['#DC2626', '#4A1F1F'], // Bright red with deep red background
  default: ['#444', 'rgb(168 204 255)'], // Default dark gray colors
};

export const getMethodColor = (method, mainTheme) => {
  const colors = mainTheme === 'dark' ? darkModeColors : lightModeColors;
  return colors[method.toLowerCase()] || colors.default;
};

export const getColorClass = (status) => {
  if (status >= 200 && status < 300) return 'success'; // Success
  if (status >= 300 && status < 400) return 'redirect'; // Redirect
  if (status >= 400 && status < 500) return 'client-error'; // Client Error
  if (status >= 500) return 'server-error'; // Server Error
  return 'unknown'; // Unknown
};

export const formatSchema = (schema, fullSpec) => {
  if (!schema) return null;

  // Handle $ref
  if (schema?.$ref) {
    const resolvedSchema = resolveRef(schema?.$ref, fullSpec);
    return formatSchema(resolvedSchema, fullSpec);
  }

  // Handle object type
  if (schema?.type === 'object' || schema?.properties) {
    const obj = {};
    if (schema?.properties) {
      Object.entries(schema?.properties ?? {}).forEach(([key, value]) => {
        if (value?.$ref) {
          obj[key] = formatSchema(value, fullSpec);
        } else if (value?.type === 'object' && value?.properties) {
          // Handle nested objects
          obj[key] = formatSchema(value, fullSpec);
        } else if (value?.anyOf) {
          obj[key] = getTypeFromSchema(value);
        } else if (value?.type === 'array') {
          // Handle arrays
          if (value?.items?.type === 'object' && value?.items?.properties) {
            obj[key] = [formatSchema(value?.items, fullSpec)];
          } else {
            obj[key] = [value?.items?.type || 'any'];
          }
        } else if (value?.enum) {
          // Handle enums
          obj[key] = value?.enum?.[0];
        } else {
          obj[key] = value?.type;
        }
      });
    }
    return obj;
  }

  // Handle array type
  if (schema?.type === 'array') {
    if (schema?.items?.type === 'object' && schema?.items?.properties) {
      return [formatSchema(schema?.items, fullSpec)];
    }
    return [schema?.items?.type || 'any'];
  }

  // Handle other types including anyOf and enums
  return getTypeFromSchema(schema);
};

const resolveRef = (ref, fullSpec) => {
  if (!ref || typeof ref !== 'string') return null;
  const path = ref.replace('#/', '').split('/');
  return path?.reduce((acc, part) => acc?.[part], fullSpec);
};

export const formatJSON = (jsonString) => {
  try {
    const parsed = JSON.parse(jsonString);
    return JSON.stringify(parsed, null, 2);
  } catch (e) {
    return jsonString;
  }
};

const getTypeFromSchema = (schema) => {
  // Handle enum
  if (schema?.enum) {
    return schema?.enum[0];
  }

  // Handle anyOf cases
  if (schema?.anyOf) {
    const types = schema?.anyOf?.map((type) => {
      if (type?.type === 'null') return 'null';
      return type?.type;
    });
    return types?.filter((t) => t !== 'null')?.join('|') || 'null';
  }

  // Handle array with items
  if (schema?.type === 'array' && schema?.items) {
    if (schema?.items?.type) {
      return `array<${schema?.items?.type}>`;
    }
    return 'array';
  }

  // Handle basic types
  return schema?.type || 'any';
};

// A function to resolve and render a response schema
export function resolveSchema(schema, definitions) {
  if (!schema) return null;

  // Handle $ref
  if (schema?.$ref) {
    const resolvedSchema = resolveRef(schema?.$ref, definitions);
    return resolveSchema(resolvedSchema, definitions);
  }

  // Handle object types
  if (schema?.type === 'object') {
    const properties = schema?.properties || {};
    const result = {};

    for (const [key, propertySchema] of Object.entries(properties ?? {})) {
      result[key] = resolveSchema(propertySchema, definitions);
    }
    return result;
  }

  // Handle array types
  if (schema?.type === 'array') {
    return [resolveSchema(schema?.items, definitions)];
  }

  // Handle anyOf
  if (schema?.anyOf) {
    // Take the first non-null schema from anyOf array
    const nonNullSchema = schema.anyOf.find((s) => s.type !== 'null');
    if (nonNullSchema) {
      return resolveSchema(nonNullSchema, definitions);
    }
    // If all schemas are null or no valid schema found, return null
    return null;
  }

  // Return example, description, or type
  return schema?.example || schema?.default || schema?.description || schema?.type || 'string';
}

export function renderResponse(response, definitions) {
  // Check if response content exists
  const responseSchema = response?.content?.['application/json']?.schema;
  if (!responseSchema) return response?.description || 'No schema found.';

  // Check if there's an example directly available
  const example = response?.content?.['application/json']?.example;
  if (example) {
    return JSON.stringify(example, null, 2);
  }

  // Resolve and render schema recursively
  return resolveSchema(responseSchema, definitions);
}

const UNSAFE_OBJECT_KEYS = new Set(['__proto__', 'prototype', 'constructor']);

function isSafeObjectKey(key) {
  return typeof key === 'string' && !UNSAFE_OBJECT_KEYS.has(key);
}

export const resolveRequestBodyRef = (schema, openApiSpec) => {
  if (!schema || typeof schema !== 'object') return schema;

  if (schema?.$ref) {
    const refPath = schema?.$ref?.replace(/^#\//, '')?.split('/');
    let resolved = openApiSpec;

    refPath?.forEach((segment) => {
      if (
        isSafeObjectKey(segment) &&
        resolved &&
        Object.prototype.hasOwnProperty.call(resolved, segment)
      ) {
        // nosemgrep: javascript.lang.security.audit.prototype-pollution.prototype-pollution-loop.prototype-pollution-loop -- segment is filtered and accessed only if it is an own safe key.
        resolved = resolved[segment];
      } else {
        resolved = undefined;
      }
    });

    return resolveRequestBodyRef(resolved, openApiSpec); // Resolve nested $ref
  }

  if (schema?.type === 'object' && schema?.properties) {
    const resolvedProperties = Object.create(null);
    Object.entries(schema.properties ?? {}).forEach(([key, value]) => {
      if (isSafeObjectKey(key)) {
        // nosemgrep: javascript.lang.security.audit.prototype-pollution.prototype-pollution-loop.prototype-pollution-loop -- unsafe keys are filtered and the target object has no prototype.
        resolvedProperties[key] = resolveRequestBodyRef(value, openApiSpec);
      }
    });
    return { ...schema, properties: resolvedProperties };
  }

  if (schema?.type === 'array' && schema?.items) {
    return { ...schema, items: resolveRequestBodyRef(schema?.items, openApiSpec) };
  }

  return schema;
};
